import * as analytics from '../../../shared/analytics/analytics.json';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  UserKeys,
  getAllResourceCalendarsWithChangeLog,
} from '../../../api/users-api';
import {
  BuildingsModel,
  FeatureModel,
  ResourceCalendar,
} from '../../../../types';
import CalendarHistoryDialog from './resource-calendar-history-dialog';
import DeleteCalendarDialog from './delete-calendar-dialog';
import ResourceCalendarShareGatedDialog from './resource-calendars-share-gated-dialog';
import ResourceCalendarsGridToolbar from './resource-calendars-grid-toolbar';
import { CalendarIcon } from '@mui/x-date-pickers';
import ResourceCalendarEventsDialog from './events/resource-calendar-events-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import ChangeLogRequiredOverlay from '../../grid-overlay/change-log-required-overlay';
import { useChangeLogGate } from '../../../shared/hooks/use-change-log-gate';
import {
  AdministrationRoles,
  CalendarRoles,
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import { useUserSettings } from '../../../utils/use-user-settings';
import { pagesConfig } from '../../../shared/pages-config';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';
import QuickReportsDialog, {
  QuickReportConfig,
} from '../../../shared/quick-reports-dialog';

import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import EventBusy from '@mui/icons-material/EventBusy';
import Share from '@mui/icons-material/Share';
import Star from '@mui/icons-material/Star';
import History from '@mui/icons-material/History';

interface ResourceCalendarsGridProps {
  calendarRoles: string[];
  changeLogRoles: string[];
  administrationRoles: string[];
  primaryDomain: string;
  loggedInUser: string;
  buildingsList: BuildingsModel[];
  buildingsLoading: boolean;
}

const reports: QuickReportConfig[] = [
  {
    displayName: 'Recurring Resource Calendar Events',
    description: 'Lists recurring calendar events.',
    reportType: 'recurringResourceCalendarEventsReport',
    role: ToolsRoles.RunCalendarReport.value,
  },
];

export default function ResourceCalendarsGrid({
  calendarRoles,
  changeLogRoles,
  administrationRoles,
  primaryDomain,
  loggedInUser,
  buildingsList,
  buildingsLoading,
}: ResourceCalendarsGridProps) {
  const navigate = useNavigate();
  const { viewType } = useUserSettings();
  const [openDeleteCalendar, setOpenDeleteCalendar] = useState(false);
  const [openShareCalendar, setOpenShareCalendar] = useState(false);
  const [openCalendarHistory, setOpenCalendarHistory] = useState(false);
  const [openEventsCalendar, setOpenEventsCalendar] = useState(false);
  const [openQuickReports, setOpenQuickReports] = useState(false);
  const [changeLogNonce, setChangeLogNonce] = useState(0);
  const [selectedCalendar, setSelectedCalendar] =
    useState<ResourceCalendar | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const changeLogGate = useChangeLogGate({
    permissionValue: CalendarRoles.ViewResourceCalendars.value,
    changeLogRoles,
  });

  const needsChangeLogMessage =
    changeLogGate.needsChangeLog &&
    !changeLogGate.confirmedChangeLogMessage.trim();

  const {
    data: calendars,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      UserKeys.ResourceCalendars,
      changeLogGate.confirmedChangeLogMessage,
      changeLogNonce,
    ],
    queryFn: () =>
      getAllResourceCalendarsWithChangeLog(
        changeLogGate.confirmedChangeLogMessage
      ),
    enabled: changeLogGate.canFetch,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('resource calendars'),
    },
  });

  const selectedCalendars = useMemo(() => {
    if (!calendars || selectedRows.length === 0) return [];
    const selectedSet = new Set(selectedRows.map(String));
    return calendars
      .filter((row) => selectedSet.has(String(row.resourceId)))
      .map((row) => row.resourceEmail)
      .filter((email): email is string => !!email);
  }, [selectedRows, calendars]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'resourceName',
        headerName: 'Calendar Name',
        flex: 1,
      },
      {
        field: 'resourceId',
        headerName: 'Calendar ID',
        flex: 1,
      },
      {
        field: 'resourceEmail',
        headerName: 'Calendar Public Email',
        flex: 1,
      },
      {
        field: 'buildingName',
        headerName: 'Building',
        flex: 1,
        valueGetter: (_value, row) => {
          const building = buildingsList.find(
            (building) => building.buildingId === row.buildingId
          );
          if (building) {
            return building.buildingName;
          } else {
            return 'None';
          }
        },
        type: 'singleSelect',
        valueOptions: buildingsList.map((building) => building.buildingName),
      },
      {
        field: 'resourceType',
        headerName: 'Type',
        valueGetter: (_value, row) => {
          return row.resourceType || 'None';
        },
      },
      {
        field: 'floorName',
        headerName: 'Floor',
        headerAlign: 'center',
        align: 'center',
        valueGetter: (_value, row) => {
          return row.floorName || 'None';
        },
      },
      {
        field: 'featureInstances',
        headerName: 'Features',
        flex: 1,
        display: 'flex',
        disableColumnMenu: true,
        renderCell: (params) => {
          if (params.value) {
            return (
              <Stack flexDirection="row" gap={1} sx={{ overflowX: 'auto' }}>
                {params.value.map((feature: FeatureModel) => (
                  <Chip
                    key={feature.etags}
                    label={feature.name}
                    size="small"
                    avatar={<Avatar children={<Star />} />}
                  />
                ))}
              </Stack>
            );
          } else {
            return 'None';
          }
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        getActions: (params) => {
          const buttonArray = [];
          if (
            isAbleToSeeUIElement(
              calendarRoles || [],
              CalendarRoles.EditResourceCalendar.value
            )
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label="Edit"
                icon={
                  <Tooltip arrow title="Edit">
                    <Edit />
                  </Tooltip>
                }
                onClick={() => {
                  navigate(
                    `/${
                      pagesConfig.resourceCalendars.link
                    }/${pagesConfig.editResourceCalendar.link.replace(
                      ':id',
                      params.row.resourceId.toString()
                    )}`
                  );
                }}
                showInMenu
              />
            );
          }

          if (
            isAbleToSeeUIElement(
              calendarRoles,
              CalendarRoles.ViewResourceCalendarShares.value
            )
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label="Share"
                icon={
                  <Tooltip arrow title="Share">
                    <Share />
                  </Tooltip>
                }
                onClick={() => {
                  setSelectedCalendar(params.row);
                  setOpenShareCalendar(true);
                }}
                showInMenu
              />
            );
          }

          if (
            isAbleToSeeUIElement(
              calendarRoles || [],
              CalendarRoles.ViewResourceCalendarEvents.value
            )
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label="Events"
                icon={
                  <Tooltip arrow title="Events">
                    <CalendarIcon />
                  </Tooltip>
                }
                onClick={() => {
                  setSelectedCalendar(params.row);
                  setOpenEventsCalendar(true);
                }}
                showInMenu
              />
            );
          }

          if (
            isAbleToSeeUIElement(
              calendarRoles || [],
              CalendarRoles.DeleteResourceCalendar.value
            )
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label="Delete"
                icon={
                  <Tooltip arrow title="Delete">
                    <Delete />
                  </Tooltip>
                }
                onClick={() => {
                  setSelectedCalendar(params.row);
                  setOpenDeleteCalendar(true);
                }}
                showInMenu
              />
            );
          }

          if (
            isAbleToSeeUIElement(
              administrationRoles,
              AdministrationRoles.ViewAppLogs.value
            )
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label="History"
                icon={
                  <Tooltip arrow title="History">
                    <History />
                  </Tooltip>
                }
                onClick={() => {
                  setSelectedCalendar(params.row);
                  setOpenCalendarHistory(true);
                }}
                showInMenu
              />
            );
          }
          return buttonArray;
        },
      },
    ],
    [administrationRoles, buildingsList, calendarRoles, navigate]
  );

  const toolbarProps = useMemo(
    () => ({
      calendarRoles,
      selectedCalendars,
      setOpenQuickReports,
    }),
    [calendarRoles, selectedCalendars]
  );

  return (
    <>
      <SharedDataGridClient
        gridKey={viewType.resourceCalendarsGridStates?.activeGridState}
        key="resource-calendars-grid"
        columns={columns}
        rows={needsChangeLogMessage ? [] : calendars || []}
        getRowId={(row) => row.resourceId}
        checkboxSelection
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        slots={{
          toolbar: useToolbar(ResourceCalendarsGridToolbar, toolbarProps),
          ...(needsChangeLogMessage
            ? {
                noRowsOverlay: () => (
                  <ChangeLogRequiredOverlay
                    onOpenDialog={() => changeLogGate.openDialog()}
                    permissionName={CalendarRoles.ViewResourceCalendars.name}
                  />
                ),
              }
            : createGridOverlays(
                'Resource Calendars',
                <EventBusy />,
                'There may be no Resource Calendars in this building.'
              )),
        }}
        disableRowSelectionOnClick
        loading={
          !needsChangeLogMessage &&
          (isLoading || isFetching || buildingsLoading)
        }
        gridStateKey="resourceCalendarsGridStates"
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'default',
          },
        }}
      />
      <ChangeLogDialog
        open={changeLogGate.openChangeLogDialog}
        handleClose={changeLogGate.dismiss}
        changeLogMessage={changeLogGate.changeLogMessage}
        setChangeLogMessage={changeLogGate.setChangeLogMessage}
        mutate={() => {
          changeLogGate.confirm(() => {
            setChangeLogNonce((prev) => prev + 1);
          });
        }}
        mutateLoading={isFetching || isLoading}
      />
      <QuickReportsDialog
        open={openQuickReports}
        handleClose={() => setOpenQuickReports(false)}
        sourceData={selectedCalendars}
        sourceType="Resource_Calendar"
        dialogTitle={`Resource Calendars - ${selectedCalendars.length} Selected`}
        analyticsEvent={analytics.calendars.resource_calendars.quick_report}
        contentDescription="Generate a one-time report for resource calendars."
        reports={reports}
        changeLogRoles={changeLogRoles}
      />
      {selectedCalendar && openShareCalendar ? (
        <ResourceCalendarShareGatedDialog
          open={openShareCalendar}
          handleClose={() => setOpenShareCalendar(false)}
          selectedCalendar={selectedCalendar}
          setSelectedCalendar={setSelectedCalendar}
          calendarRoles={calendarRoles}
          changeLogRoles={changeLogRoles}
          primaryDomain={primaryDomain}
          loggedInUser={loggedInUser}
        />
      ) : null}
      {selectedCalendar && openDeleteCalendar ? (
        <DeleteCalendarDialog
          open={openDeleteCalendar}
          handleClose={() => setOpenDeleteCalendar(false)}
          selectedCalendar={selectedCalendar}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {selectedCalendar && openCalendarHistory ? (
        <CalendarHistoryDialog
          open={openCalendarHistory}
          handleClose={() => setOpenCalendarHistory(false)}
          selectedCalendar={selectedCalendar}
        />
      ) : null}
      {selectedCalendar && openEventsCalendar ? (
        <ResourceCalendarEventsDialog
          open={openEventsCalendar}
          handleClose={() => setOpenEventsCalendar(false)}
          userEmail={loggedInUser}
          selectedCalendar={selectedCalendar}
          calendarRoles={calendarRoles}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
    </>
  );
}
